import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';

/**
 * Route guard that enforces permission-based access control.
 *
 * Route configuration — single permission (string):
 *   data: { permission: 'Agent_Manage' }
 *
 * Route configuration — any of multiple permissions (string[]):
 *   data: { permission: ['Marketing_View', 'Marketer_Permissions', 'Telemarketer_Permissions'] }
 *
 * Rules:
 *   - No `permission` in route.data → allowed (authGuard handles auth separately).
 *   - String: user must have that exact permission.
 *   - String[]: user must have AT LEAST ONE of the listed permissions (OR logic).
 *   - Fail → redirect to /unauthorised.
 *
 * Why arrays? The backend may return either granular permissions (Marketing_View)
 * OR composite sentinels (Marketer_Permissions) depending on rbac.config.json.
 * Arrays let routes declare all valid access tokens for the same page.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {
    constructor(
        private permissionService: PermissionService,
        private router: Router
    ) { }

    canActivate(route: ActivatedRouteSnapshot): boolean {
        const required: string | string[] | undefined = route.data?.['permission'];

        // No permission constraint — route is guarded only by authGuard.
        if (!required) return true;

        const allowed = Array.isArray(required)
            ? this.permissionService.hasAny(...required)   // OR logic over array
            : this.permissionService.has(required);        // exact single match

        if (allowed) return true;

        // Log to help diagnose denied access.
        console.warn(
            '[PermissionGuard] Access denied.',
            '\n  Required:', required,
            '\n  Loaded:', this.permissionService.isPermissionsLoaded()
        );

        if (!this.permissionService.isPermissionsLoaded()) {
            // Permissions not yet fetched — go home to re-trigger the login flow.
            this.router.navigate(['/']);
        } else {
            this.router.navigate(['/unauthorised']);
        }
        return false;
    }
}
